import React, { useState } from 'react';
import { generateMockParentInsights } from '../../../utils/aiMockDataGenerator';
import {
  Sparkles,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Users,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const Step2_ParentInsightsGenerator = ({ wizardData, updateData, goToNextStep }) => {
  const { profile, parentInsights, parentContextNotes } = wizardData;
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expandedParent, setExpandedParent] = useState('parentA');

  const generateInsights = async () => {
    setGenerating(true);
    setError(null);

    try {
      const insights = await generateMockParentInsights(profile, parentContextNotes);
      updateData('parentInsights', insights);
    } catch (err) {
      console.error('Error generating insights:', err);
      setError('Failed to generate parent insights. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const updateParentInsight = (parent, field, value) => {
    updateData('parentInsights', {
      ...parentInsights,
      [parent]: {
        ...parentInsights[parent],
        [field]: value
      }
    });
  };

  const hasGeneratedInsights = parentInsights?.parentA && Object.keys(parentInsights.parentA).length > 0;

  const parentAFields = [
    { key: 'easy_subject', label: 'What subject or activity does your child find surprisingly easy?' },
    { key: 'growth', label: 'Describe a time when you watched your child grow through a challenge' },
    { key: 'learning_difference', label: 'When has your child\'s way of thinking been different?' },
    { key: 'surprises', label: 'What does your child do that consistently surprises adults?' },
    { key: 'age_limit', label: 'Has your child been told they couldn\'t do something due to age?' },
    { key: 'rules_questioned', label: 'What rules has your child questioned that made you think "they have a point"?' },
    { key: 'wrong_level', label: 'Has your child been placed in the \'wrong\' level for something?' },
    { key: 'advocacy_success', label: 'Describe a time your child advocated for themselves or others' },
    { key: 'initiative_against_advice', label: 'When has your child taken initiative despite advice against it?' },
    { key: 'mechanical_skills', label: 'What practical or mechanical skills does your child have?' },
    { key: 'math_integration', label: 'When does your child bring mathematical thinking into unexpected situations?' },
    { key: 'entrepreneurial_instincts', label: 'When has your child shown entrepreneurial instincts?' },
    { key: 'obsessions', label: 'What does your child get obsessed with?' },
    { key: 'unusual_interests', label: 'What\'s something your child cares deeply about that doesn\'t seem typical?' },
    { key: 'hidden_qualities', label: 'What do you hope college admissions officers understand about your child?' }
  ];

  const parentBFields = [
    ...parentAFields,
    { key: 'different_perspective', label: 'Where do you and your spouse see your child differently?' },
    { key: 'what_other_parent_misses', label: 'What does your spouse miss about your child that you see clearly?' }
  ];

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Parent Insights Generator</h2>
        <p className="text-slate-600">
          Generate realistic parent observations based on {profile.name || 'the student'}&#39;s profile. You can review and edit them before proceeding.
        </p>
      </div>

      {/* Quick Context Notes - Always visible */}
      <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-yellow-900 mb-2 flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          Quick Context Notes {!hasGeneratedInsights && '(Optional but Recommended)'}
        </h3>
        <p className="text-sm text-yellow-700 mb-4">
          {hasGeneratedInsights
            ? 'Update these notes and regenerate to incorporate new context into the parent insights.'
            : 'Add any key facts, anecdotes, or context that will help generate more accurate parent insights.'
          } Examples: "skipped 7th grade", "tutored little sister in math", "built a treehouse from scratch",
          "likes to do things his own way but excellent problem solver", "struggled with reading until 5th grade"
        </p>
        <textarea
          value={parentContextNotes || ''}
          onChange={(e) => updateData('parentContextNotes', e.target.value)}
          rows={4}
          className="w-full px-4 py-3 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
          placeholder="Quick notes about the student that would inform parent observations..."
        />
        <p className="text-xs text-yellow-600 mt-2">
          💡 These notes will be incorporated into the AI-generated parent insights to make them more realistic
        </p>
      </div>

      {/* Generate Button */}
      {!hasGeneratedInsights && (
        <div className="bg-gradient-to-br from-azure-50 to-violet-50 border-2 border-azure-300 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-azure-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Ready to Generate Parent Insights?</h3>
          <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
            Using AI, we'll create realistic parent observations based on {profile.name || 'the student'}&#39;s
            academic profile. This includes insights about natural abilities, learning style, unique qualities,
            and growth patterns.
          </p>
          <button
            onClick={generateInsights}
            disabled={generating}
            className="px-8 py-4 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto"
          >
            {generating ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Generating Insights...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Parent Insights
              </>
            )}
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-red-900 mb-1">Generation Failed</h4>
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <AlertCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Generated Insights - Editable */}
      {hasGeneratedInsights && (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-emerald-900 mb-1">Parent Insights Generated!</h4>
              <p className="text-sm text-emerald-700">
                Review and edit the insights below. Update the context notes above and click "Regenerate" to incorporate new details, or modify individual fields directly.
              </p>
            </div>
            <button
              onClick={generateInsights}
              disabled={generating}
              className="text-emerald-600 hover:text-emerald-800 flex items-center gap-2 text-sm font-medium disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Regenerating...' : 'Regenerate'}
            </button>
          </div>

          {/* Parent A Observations */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedParent(expandedParent === 'parentA' ? null : 'parentA')}
              className="w-full flex items-center justify-between p-4 bg-azure-50 hover:bg-azure-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-azure-600" />
                <h3 className="text-lg font-semibold text-slate-900">Parent A Observations</h3>
                <span className="text-sm text-slate-600">
                  ({parentAFields.filter(f => parentInsights.parentA?.[f.key]?.trim()).length} / {parentAFields.length} filled)
                </span>
              </div>
              {expandedParent === 'parentA' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {expandedParent === 'parentA' && (
              <div className="p-4 space-y-4 bg-white">
                {parentAFields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {field.label}
                    </label>
                    <textarea
                      value={parentInsights.parentA?.[field.key] || ''}
                      onChange={(e) => updateParentInsight('parentA', field.key, e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500 resize-none"
                      placeholder="Parent's observation..."
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Parent B Observations */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedParent(expandedParent === 'parentB' ? null : 'parentB')}
              className="w-full flex items-center justify-between p-4 bg-violet-50 hover:bg-violet-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-violet-600" />
                <h3 className="text-lg font-semibold text-slate-900">Parent B Observations</h3>
                <span className="text-sm text-slate-600">
                  ({parentBFields.filter(f => parentInsights.parentB?.[f.key]?.trim()).length} / {parentBFields.length} filled)
                </span>
              </div>
              {expandedParent === 'parentB' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {expandedParent === 'parentB' && (
              <div className="p-4 space-y-4 bg-white">
                {parentBFields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {field.label}
                    </label>
                    <textarea
                      value={parentInsights.parentB?.[field.key] || ''}
                      onChange={(e) => updateParentInsight('parentB', field.key, e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                      placeholder="Parent's observation..."
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Next Step Info */}
          <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-azure-900 mb-1">Next Step: Strategic Context</h4>
                <p className="text-sm text-azure-700">
                  These parent insights will be combined with the student profile to generate a strategic context summary and personality profile.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step2_ParentInsightsGenerator;
